import React, {
  useEffect,
  createRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Box,
  GDSStyleObject,
  HeadingText,
  FormField,
  Checkbox,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import COBQuestioner from '../../../../assets/json/COB_QUESTIONER.json';
import { Calendar } from '../../../reusable-components/calendar/calendar';
import { isEmpty, isFutureDate } from '../../../helpers/helpers';
import useCobStore from '../../../../store/cob-store';
import '../../forms.css';
import { COBLayout } from '../../../cob-layout/cob-layout';

const CHECK_TYPE = {
  AGE: 'age_chx',
  DISABILITY: 'dis_chx',
  END_STAGE: 'endStg_chx',
};

const intialValue = {
  disabilityBeginDate: '',
  eligibilityCriteria: '',
};

export const EntitlementReason = forwardRef((props: any, ref) => {
  const { errorHandling, OPISResponseInfo, nextDisabled } = props;
  const [isAgeChecked, setIsAgeChecked] = useState(false);
  const [isDisabilityChecked, setIsDisabilityChecked] =
    useState<boolean>(false);
  const [isEndStageRenalChecked, setIsEndStageRenalChecked] =
    useState<boolean>(false);
  const [isEmptyDate, setEmptyDate] = useState<boolean>(false);

  const [dateHeading, setDateHeading] = useState<string>('');
  const [disabilityBeginDateError, setDisabilityBeginDateError] =
    useState<string>('');
  const [form, setForm] = useState(intialValue);
  const [errors, setErrors] = useState(false);
  const disabilityCheckboxRef = createRef<HTMLInputElement>();
  const renalCheckboxRef = createRef<HTMLInputElement>();

  const dependantList = useCobStore((state: any) => state.demographics);
  const isBelow400 = useMediaQuery(queryHelpers.down(400));
  const isBelow380 = useMediaQuery(queryHelpers.down(380));
  let medicareCriteriaReason: any = '';

  const isDisabilityDate = isDisabilityChecked || isEndStageRenalChecked;

  const isCheckBoxSelected =
    isAgeChecked || isDisabilityChecked || isEndStageRenalChecked;

  const formField =
    isEmpty(form.disabilityBeginDate) || isFutureDate(form.disabilityBeginDate);

  useEffect(() => {
    if (isCheckBoxSelected && OPISResponseInfo.eligibilityCriteria !== '') {
      nextDisabled(false);
    } else {
      nextDisabled(true);
    }
    if (OPISResponseInfo.eligibilityCriteria !== '') {
      if (OPISResponseInfo?.eligibilityCriteria === '3') {
        setIsDisabilityChecked(true);
        setIsEndStageRenalChecked(true);
      }
      if (OPISResponseInfo?.eligibilityCriteria === '2') {
        setIsEndStageRenalChecked(true);
        setIsAgeChecked(false);
      }
      if (OPISResponseInfo?.eligibilityCriteria === '1') {
        setIsDisabilityChecked(true);
      }
      if (OPISResponseInfo?.eligibilityCriteria === '0') {
        setIsAgeChecked(true);
      }
    }
  }, [nextDisabled, isCheckBoxSelected, OPISResponseInfo.eligibilityCriteria]);

  useEffect(() => {
    if (isDisabilityDate && formField) {
      errorHandling(false);
    } else {
      errorHandling(true);
    }
  }, [errorHandling, formField, isDisabilityDate, form]);

  useImperativeHandle(ref, () => ({
    callValidation() {
      if (
        isEmpty(form.disabilityBeginDate) ||
        isEmpty(OPISResponseInfo.disabilityBeginDate)
      ) {
        setDisabilityBeginDateError(COBQuestioner.COB.ERRORS.REQUIREDFIELD);
        setErrors(true);
      } else if (
        isFutureDate(form.disabilityBeginDate) ||
        isFutureDate(OPISResponseInfo?.disabilityBeginDate)
      ) {
        setDisabilityBeginDateError(COBQuestioner.COB.ERRORS.VALIDDATE);
        setErrors(true);
      } else {
        setDisabilityBeginDateError('');
        setErrors(false);
      }
    },
  }));

  const handleDateHeading = () => {
    if (
      isDisabilityChecked ||
      (isDisabilityChecked && isEndStageRenalChecked)
    ) {
      setDateHeading(
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DISABILITY,
      );
    } else if (isEndStageRenalChecked) {
      setDateHeading(
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.ENDSTAGERENALDISEASE,
      );
    }
  };

  useEffect(() => {
    handleDateHeading();
  });

  useEffect(() => {
    if (OPISResponseInfo.disabilityBeginDate) {
      form.disabilityBeginDate = OPISResponseInfo.disabilityBeginDate;
    }
  }, [OPISResponseInfo, form]);

  const checkMedicalReason = () => {
    if (
      disabilityCheckboxRef?.current?.checked &&
      renalCheckboxRef.current?.checked
    ) {
      medicareCriteriaReason =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.THREE;
      form.eligibilityCriteria =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.THREE;
      OPISResponseInfo.eligibilityCriteria =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.THREE;
    } else if (
      disabilityCheckboxRef.current?.checked &&
      !renalCheckboxRef.current?.checked
    ) {
      medicareCriteriaReason =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.ONE;
      form.eligibilityCriteria =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.ONE;
      OPISResponseInfo.eligibilityCriteria =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.ONE;
    } else if (
      !disabilityCheckboxRef.current?.checked &&
      renalCheckboxRef.current?.checked
    ) {
      medicareCriteriaReason =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.TWO;
      form.eligibilityCriteria =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.TWO;
      OPISResponseInfo.eligibilityCriteria =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.TWO;
    } else {
      medicareCriteriaReason =
        COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.ZERO;
    }
  };

  const disabilityBeginDate = (selectedDate: string) => {
    setForm((prevState) => ({
      ...prevState,
      disabilityBeginDate: selectedDate,
    }));
    checkMedicalReason();
    form.eligibilityCriteria = medicareCriteriaReason;
    form.disabilityBeginDate = selectedDate;
    OPISResponseInfo.eligibilityCriteria = medicareCriteriaReason;
    OPISResponseInfo.disabilityBeginDate = selectedDate;
    setErrors(!selectedDate);
    setEmptyDate(false);
  };

  const handleInputChange = (event: any) => {
    const { id } = event.target;
    switch (id) {
      case CHECK_TYPE.AGE:
        setIsAgeChecked(!isAgeChecked);
        setIsDisabilityChecked(false);
        setIsEndStageRenalChecked(false);
        checkMedicalReason();
        if (event.target.checked) {
          form.eligibilityCriteria =
            COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.ZERO;
          OPISResponseInfo.eligibilityCriteria =
            COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.REASON.ZERO;
          OPISResponseInfo.disabilityBeginDate = '';
          form.disabilityBeginDate = '';
        } else {
          form.eligibilityCriteria = '';
          OPISResponseInfo.eligibilityCriteria = '';
        }
        setEmptyDate(false);
        break;
      case CHECK_TYPE.DISABILITY:
        setIsDisabilityChecked(!isDisabilityChecked);
        checkMedicalReason();
        setIsAgeChecked(false);
        setEmptyDate(false);
        if (isEndStageRenalChecked) {
          form.disabilityBeginDate = '';
          OPISResponseInfo.disabilityBeginDate = '';
          setEmptyDate(true);
        }
        break;
      case CHECK_TYPE.END_STAGE:
        setIsEndStageRenalChecked(!isEndStageRenalChecked);
        checkMedicalReason();
        setIsAgeChecked(false);
        setEmptyDate(false);
        break;
      default:
        break;
    }

    if (
      dateHeading !==
      COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DISABILITY
    ) {
      if (isEndStageRenalChecked) {
        if (isDisabilityChecked) {
          form.disabilityBeginDate = '';
          OPISResponseInfo.disabilityBeginDate = '';
          setEmptyDate(true);
        }
      }
    }
  };

  return (
    <COBLayout
      title={
        OPISResponseInfo?.member?.firstName ===
          dependantList.demographics.firstName &&
        OPISResponseInfo.member.dateOfBirth ===
          dependantList?.demographics?.dateOfBirth
          ? COBQuestioner.COB.INFORMATION.YOURMEDICARE
          : `${`${OPISResponseInfo.member.firstName} ${OPISResponseInfo.member.lastName}${COBQuestioner.COB.INFORMATION.DEPENDENTMEDICARE}`}`
      }
      dataTestId="entitlement"
      questionTitle={
        OPISResponseInfo?.member?.firstName ===
        dependantList.demographics.firstName
          ? COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.TITLE
          : `${`${COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DEPENDENTTITLE}`}${
              OPISResponseInfo.member.firstName
            }${
              COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT
                .CONTINUOUSDEPENDANTTITLE
            }`
      }
      disclaimer={COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DISCLAIMER}
      isDependant={false}
    >
      <Box
        css={{
          width: isBelow380 ? '332px' : '368px',
        }}
      >
        <Box className="GDS-checkbox-warapper marginBottom15">
          <Checkbox
            label={COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.AGEOVER}
            name="ageChx"
            id="age_chx"
            checked={
              OPISResponseInfo.eligibilityCriteria === '0' ? true : isAgeChecked
            }
            onClick={handleInputChange}
            layout="row"
          />
        </Box>
        <Box className="GDS-checkbox-warapper marginBottom15">
          <Checkbox
            label={COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DISABILITY}
            name="disChx"
            checked={
              OPISResponseInfo.eligibilityCriteria === '1' ||
              OPISResponseInfo.eligibilityCriteria === '3'
                ? true
                : isDisabilityChecked
            }
            onClick={handleInputChange}
            id="dis_chx"
            layout="row"
            ref={disabilityCheckboxRef}
          />
        </Box>
        <Box className="GDS-checkbox-warapper marginBottom15">
          <Checkbox
            label={
              COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.ENDSTAGERENAL
            }
            name="endStgChx"
            checked={
              OPISResponseInfo.eligibilityCriteria === '2' ||
              OPISResponseInfo.eligibilityCriteria === '3'
                ? true
                : isEndStageRenalChecked
            }
            onClick={handleInputChange}
            id="endStg_chx"
            layout="row"
            ref={renalCheckboxRef}
          />
        </Box>
        {isEndStageRenalChecked || isDisabilityChecked ? (
          <Box>
            <HeadingText
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'xl',
                '@mobileLandscape': 'xl',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
              css={
                { marginTop: '$half', marginBottom: '$one' } as GDSStyleObject
              }
            >
              {dateHeading}
            </HeadingText>
            <FormField
              id="disabilityBeginDate"
              data-testid="active-date"
              label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DISABILITYDATE
              }
              aria-label={
                COBQuestioner.COB.QUESTIONS.CONTROLS.ENTITLEMENT.DISABILITYDATE
              }
              css={
                {
                  marginBottom: '$one',
                  '& label': {
                    marginBottom: '10px',
                  },
                } as GDSStyleObject
              }
            >
              <Calendar
                selectedFormatDate={disabilityBeginDate}
                data-testid="disability-date"
                currentDate={
                  OPISResponseInfo.disabilityBeginDate
                    ? OPISResponseInfo.disabilityBeginDate
                    : form.disabilityBeginDate
                }
                showError={errors}
                errorMessage={disabilityBeginDateError}
                isEmptyDate={isEmptyDate}
                setWidth={isBelow400 ? '105px' : '116px'}
                isFutureLimitYear
              />
            </FormField>
          </Box>
        ) : (
          ''
        )}
      </Box>
    </COBLayout>
  );
});
